<template>
	<div
		v-show="teaserStore.state.isPreviewing"
		:style="{
			top: windowCoords?.top + 'px' || 0,
			left: windowCoords?.left + 'px' || 0,
		}"
		:class="[
			'teaser',
			`${teaserSize.replace('_', '-')}`,
			teaserStore.state.isFirstVisibleElement ? 'is-first' : null,
			teaserStore.state.isLastVisibleElement ? 'is-last' : null,

			'teaser-replica',
			{
				'is-previewing': teaserStore.state.isPreviewing && !teaserStore.state.isClosing,
				'is-closing': teaserStore.state.isClosing === true,
			},
		]"
		@mouseenter="teaserStore.handleMouseOver()"
		@mouseleave="handleMouseLeave"
	>
		<div class="preview-element" :class="{ 'is-web': !isNativeDevice }" ref="teaserElement">
			<!-- <transition :name="isNativeDevice ? '' : 'fade'"> -->
			<img @click="play()" v-if="!showVideo && movie" :src="imageUrl" :alt="movie.field_image?.meta?.alt" />
			<!-- </transition> -->
			<transition :name="isNativeDevice ? '' : 'fade'">
				<div @click="play()" class="fade-container" v-show="showVideo">
					<div class="player-wrapper">
						<div
							v-if="movie"
							ref="playerElement"
							:id="`kwik-player-preview-${movie.id}`"
							class="kwik-player"
							autoplay
							muted
							playsinline
						></div>
					</div>
				</div>
			</transition>

			<Transition :name="isNativeDevice ? '' : 'fade'">
				<div class="preview-section" v-if="showPreviewSection">
					<ActionButtons
						v-if="!isNativeDevice && movie"
						v-focus-section
						:item="movie"
						:type="movie.type"
						:player="`preview-${movie.id}`"
						:episode_nid="selectedEpisodeNid"
						:isHomeTeaser="true"
					/>
					<div v-if="movie" class="preview-info">
						<div class="preview-info-duration">
							<h2 v-focus>
								{{ movie.title }} <span v-if="episodeInfo">{{ episodeInfo }}</span>
							</h2>
							<div
								v-if="timeline !== undefined"
								class="video-timeline-progress"
								:style="{
									background: `linear-gradient(to right, #f13c36 0%, #f13c36 ${timeline}%, #ffffff ${timeline}%, #ffffff 100%)`,
								}"
							></div>
						</div>
						<p class="duration" v-if="movie.field_duration">{{ movie.field_duration }}</p>
					</div>
				</div>
			</Transition>
		</div>
	</div>
</template>

<script lang="ts" setup>
	import { computed, ref, watch, Ref, onMounted } from "vue"
	import { Movie } from "../../types/Movie"
	import { Episode } from "../../types/Episode"
	import ActionButtons from "../ActionButtons.vue"
	import { isNativeDevice, url } from "../../helper"
	import { useTeaserStore } from "../../stores/teaser"
	import { useTimestampStore } from "../../stores/timestamps"
	import { useUserProfiles } from "../../composables/useUserProfile"
	import { extractDurationFromKwik } from "../../utils/media"
	import api from "../../composables/useApi"
	import { useRouter } from "vue-router"
	import { useI18n } from "vue-i18n"
	const { locale } = useI18n()
	const nextEpisodeId = ref<string | null>(null)
	const router = useRouter()
	const { fetchVideoFlagStoreByProfile } = useUserProfiles()
	const showPreviewSection = computed(() => teaserStore.state.showPreviewSection)
	const showVideo = ref(false)
	const timeline: Ref<number | undefined> = ref(undefined)
	const teaserElement = ref<HTMLElement | null>(null)
	const playerElement = ref<HTMLElement | null>(null)
	const timestampStore = useTimestampStore()
	const teaserStore = useTeaserStore()
	const movie = computed(() => teaserStore.state.selectedItem)
	const teaserSize = computed(() => teaserStore.state.teaserSize || "teaser_large")
	const windowCoords = computed(() => teaserStore.state.windowCoords)

	const props = withDefaults(
		defineProps<{
			// movie: Movie
			isPreviewing?: boolean
			isSubComponent?: boolean
			teaserSize?: string
		}>(),
		{
			isPreviewing: false,
			isSubComponent: false,
			teaserSize: "teaser_large",
		}
	)
	const entityType = ref("")
	const duration = ref(0)
	const episodeInfo = ref("")
	const selectedEpisodeNid = ref<number>(0)
	const emit = defineEmits<{
		(e: "episodeSelected", nid: number): void
	}>()

	const imageUrl = computed(() => {
		return (
			movie.value?.field_image?.image_style_uri?.[props.teaserSize] ||
			movie.value?.field_image?.image_style_uri?.teaser_large ||
			undefined
		)
	})
	const time: Ref<number | undefined> = ref(undefined)
	watch(
		() => teaserStore.state.isPreviewing,
		async (newVal: boolean) => {
			if (!newVal) {
				showVideo.value = false
				// if (typeof window.kwikMotion === "function" && teaserStore.state.playerRef) {
				window.kwikMotion(`kwik-player-preview-${movie.value?.id}`).dispose()
				// }
			} else {
				const entityId = movie.value?.type === "node--movie" ? (movie.value as Movie)?.id : undefined
				const parentId = movie.value?.type === "node--series" ? (movie.value as Series).id : undefined
				const flag: Ref<string | undefined> = ref("")
				if (movie.value?.type === "node--movie") {
					entityType.value = "movie"
					flag.value = "started"
				} else if (movie.value?.type === "node--series" || movie.value?.type === "series") {
					entityType.value = "episode"
					flag.value = undefined
				}
				const res = await fetchVideoFlagStoreByProfile(entityId, entityType.value, flag.value, parentId)

				if (res.length && res[0].video_flag_type === "completed") {
					let episodes: string[] = []
					try {
						const res = await api.get(`/jsonapi/node/episode`, {
							params: {
								"filter[field_series.id]": parentId,
								"fields[node--episode]": "drupal_internal__nid",
							},
						})
						episodes = res.data.data.map((ep: any) => ep.attributes.drupal_internal__nid)
					} catch (error) {
						return
					}
					const currentId = res[0]?.target_entity.drupal_internal__nid
					const currentIndex = episodes.indexOf(currentId)
					if (currentIndex >= 0 && currentIndex < episodes.length - 1) {
						nextEpisodeId.value = episodes[currentIndex + 1]
						const response = await api.get(
							`${locale.value}/jsonapi/node/episode?jsonapi_include=1&include=field_trailer_file,field_image,field_series,field_series.field_image,field_subtitles,field_subtitles.field_subtitle_file`,
							{
								params: {
									"filter[drupal_internal__nid]": nextEpisodeId.value,
									"filter[langcode]": locale.value,
									"filter[status]": 1,
								},
							}
						)
						selectedEpisodeNid.value = response.data.data[0].drupal_internal__nid
						if (response.data.data[0]) {
							episodeInfo.value = `- S:${response.data.data[0].field_season} E:${response.data.data[0].title}`
						}
					}
				} else if (res.length === 0 && (movie.value?.type === "node--series" || movie.value?.type === "series")) {
					time.value = 0
					await api
						.get(`${url}/jsonapi/node/episode`, {
							params: {
								"filter[field_series.id]": parentId,
								page: {
									limit: 1,
								},
							},
						})
						.then((response) => {
							if (response.data.data && response.data.data.length > 0) {
								const episodeData = response.data.data[0]
								// duration.value = extractDurationFromKwik((episodeData as Episode)?.field_kwik_data) ?? 0
								selectedEpisodeNid.value = episodeData.attributes.drupal_internal__nid
							}
						})
				} else {
					duration.value = extractDurationFromKwik((movie.value as any)?.field_kwik_data) ?? 0
					if (movie.value?.type === "node--movie") {
						timestampStore.addTimestamp(res[0]?.timestamp, entityType.value, movie.value.drupal_internal__nid)
					}
					if (res[0]?.timestamp) {
						time.value = res[0]?.timestamp
						timestampStore.addTimestamp(res[0]?.timestamp, entityType.value, movie.value.drupal_internal__nid)

						duration.value = extractDurationFromKwik((res[0]?.target_entity as Episode)?.field_kwik_data) ?? 0
						time.value = res[0]?.timestamp
						//
						// episodeInfo.value = ""
						if (res[0]?.target_entity_type === "episode") {
							// timestampStore.addTimestamp(res[0]?.timestamp, "node--episode", res[0]?.target_entity.drupal_internal__nid)
							episodeInfo.value = `- S:${res[0]?.target_entity?.field_season} E:${res[0]?.target_entity?.title}`

							selectedEpisodeNid.value = res[0]?.target_entity?.drupal_internal__nid
							emit("episodeSelected", selectedEpisodeNid.value)
						}
					}
					if (duration.value > 0 && time.value > 0) {
						timeline.value = (time.value / duration.value) * 100
					}
				}

				/**
				 * This block works for both JSON:API results, where field_trailer is an object,
				 * and for the SearchAPI results, where field_trailer is a string.
				 */
				if (
					movie.value?.field_trailer_file.data !== null ||
					(movie.value?.field_trailer && movie.value?.field_trailer?.uri) ||
					typeof movie.value?.field_trailer === "string"
				) {
					const videoUrl =
						movie.value?.field_trailer_file.data !== null
							? `${url}${movie.value?.field_trailer_file.uri.url}`
							: movie.value?.field_trailer?.uri
							? movie.value?.field_trailer?.uri
							: movie.value?.field_trailer
					const res = await api.get("/api/sign-url", {
						params: {
							uuid: movie.value?.id,
						},
						headers: {
							Accept: "application/vnd.api+json",
						},
					})

					teaserStore.options.src = res.data.signed_url

					// if (typeof window.kwikMotion === "function" && teaserStore.state.playerRef) {
					// 	window.kwikMotion(`kwik-player-preview-${movie.value.id}`).dispose()
					// }
					window.initPlayer(`kwik-player-preview-${movie.value.id}`, teaserStore.options, () => {
						teaserStore.state.playerRef = window.kwikMotion(`kwik-player-preview-${movie.value.id}`)
					})
					// teaserStore.state.playerRef.src(videoUrl)
					setTimeout(() => {
						showVideo.value = true
					}, 500)
				} else {
					showVideo.value = false
					// teaserStore.state.playerRef.reset()
				}
			}
		}
	)

	const handleMouseLeave = () => {
		// if (teaserStore.state.isPreviewing) {
		teaserStore.state.isClosing = true
		teaserStore.state.showPreviewSection = false
		timeline.value = undefined
		time.value = undefined
		entityType.value = ""
		duration.value = 0
		episodeInfo.value = ""
		selectedEpisodeNid.value = 0

		// teaserStore.handleWebMouseLeave()
		setTimeout(() => {
			teaserStore.state.isClosing = undefined
			teaserStore.state.isPreviewing = false
			teaserStore.state.isFirstVisibleElement = undefined
			teaserStore.state.isLastVisibleElement = undefined
		}, 100)
		// }
	}

	onMounted(() => {
		// 	if (typeof window.kwikMotion === "function" && teaserStore.state.playerRef) {
		// 		window.kwikMotion("kwik-player-preview").dispose()
		// 	}
		// 	window.initPlayer("kwik-player-preview", teaserStore.options, () => {
		// 		teaserStore.state.playerRef = window.kwikMotion("kwik-player-preview")
		// 	})
	})

	const play = () => {
		teaserStore.play(
			movie.value?.type.includes("movie") ? "movie" : "episode",
			movie.value!,
			movie.value?.type.includes("movie") ? undefined : selectedEpisodeNid.value,
			router
		)
	}
</script>

<style scoped lang="scss">
	img {
		width: 100%;
		display: block;
		height: auto;
		transition: all 0.3s ease;
		object-fit: cover;
		border-radius: 12px;
		cursor: pointer;
	}

	.duration {
		flex-shrink: 0;
	}

	.preview-info {
		display: flex;
		justify-content: space-between;
		align-items: flex-start;
		margin-top: 15px;
		.preview-info-duration {
			display: flex;
			flex-direction: column;
			gap: 12px;
			h2 {
				font-size: 26px;
				padding-right: 5px;
				@include br.respond-to(1560px) {
					font-size: 22px;
				}

				@include br.respond-to(br.$tablet-medium) {
					font-size: 20px;
				}

				@include br.respond-to(br.$mobile-xlarge) {
					font-size: 18px;
				}
			}
		}
		p {
			font-size: 14px;
			line-height: 38px;
		}
	}

	.video-timeline-progress {
		height: 1px;
	}
</style>

<style lang="scss">
	.teaser-small,
	.teaser-medium,
	.teaser-with-number {
		// Define CSS variables for dimensions
		--teaser-initial-width: 263px;
		--teaser-initial-height: 372px;
		--teaser-expanded-width: 384px;
		--teaser-expanded-height: 438px;

		@include br.respond-to(1360px) {
			--teaser-initial-width: 220px;
			--teaser-initial-height: 312px;
			--teaser-expanded-width: 320px;
			--teaser-expanded-height: 370px;
		}

		@include br.respond-to(br.$tablet-medium) {
			--teaser-initial-width: 200px;
			--teaser-initial-height: 282px;
			--teaser-expanded-width: 300px;
			--teaser-expanded-height: 342px;
		}

		&:not(.is-first):not(.is-last) {
			&.is-previewing {
				animation: expandSmallTeaser 0.3s cubic-bezier(0.1, 0, 0.2, 1) forwards;
				border: 1px solid rgba(255, 255, 255, 0.1);

				.preview-element {
					&.is-web {
						@keyframes expandSmallTeaser {
							0% {
								transform: translate(0px, var(--teaser-container-height));
								opacity: 0;
								box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
								width: var(--teaser-initial-width);
								height: var(--teaser-initial-height);
							}
							45% {
								opacity: 0;
							}
							100% {
								opacity: 1;
								transform: translate(-70px, var(--teaser-container-height));
								box-shadow: 0 8px 400px 100px rgba(0, 0, 0, 0.8);
								width: var(--teaser-expanded-width);
								height: var(--teaser-expanded-height);
							}
						}
					}
				}
			}
			// Reverse animation when closing
			&.is-closing {
				animation: collapseSmallTeaser 0.4s cubic-bezier(0.1, 0, 0.2, 1) forwards;

				.preview-element {
					&.is-web {
						@keyframes collapseSmallTeaser {
							0% {
								opacity: 1;
								transform: translate(-70px, var(--teaser-container-height));
								box-shadow: 0 8px 40px rgba(0, 0, 0, 0.8);
								width: var(--teaser-expanded-width);
								height: var(--teaser-expanded-height);
							}
							45% {
								opacity: 0;
							}
							100% {
								transform: translate(0px, 0px);
								opacity: 0;
								box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
								width: var(--teaser-initial-width);
								height: var(--teaser-initial-height);
							}
						}
					}
				}
			}
		}
		&.is-first {
			&.is-previewing {
				animation: expandSmallTeaserFirst 0.4s cubic-bezier(0.1, 0, 0.2, 1) forwards;
				border: 1px solid rgba(255, 255, 255, 0.1);

				.preview-element {
					&.is-web {
						@keyframes expandSmallTeaserFirst {
							0% {
								transform: translate(-1px, 0px);
								opacity: 0;
								box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
								width: var(--teaser-initial-width);
								height: var(--teaser-initial-height);
							}
							45% {
								opacity: 0;
							}
							100% {
								opacity: 1;
								// Keep it aligned to the left, only move up
								transform: translate(-1px, var(--teaser-container-height));
								box-shadow: 0 8px 40px rgba(0, 0, 0, 0.8);
								width: var(--teaser-expanded-width);
								height: var(--teaser-expanded-height);
							}
						}
					}
				}
			}
			&.is-closing {
				animation: collapseSmallTeaserFirst 0.4s cubic-bezier(0.1, 0, 0.2, 1) forwards;

				.preview-element {
					&.is-web {
						@keyframes collapseSmallTeaserFirst {
							0% {
								opacity: 1;
								transform: translate(0px, var(--teaser-container-height));
								box-shadow: 0 8px 40px rgba(0, 0, 0, 0.8);
								width: var(--teaser-expanded-width);
								height: var(--teaser-expanded-height);
							}
							45% {
								opacity: 0;
							}
							100% {
								transform: translate(0px, 0px);
								opacity: 0;
								box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
								width: var(--teaser-initial-width);
								height: var(--teaser-initial-height);
							}
						}
					}
				}
			}
		}

		// case for one item in the slider fix
		&.is-first {
			&.is-previewing {
				animation: expandSmallTeaserFirst 0.4s cubic-bezier(0.1, 0, 0.2, 1) forwards;
				border: 1px solid rgba(255, 255, 255, 0.1);

				.preview-element {
					&.is-web {
						@keyframes expandSmallTeaserFirst {
							0% {
								transform: translate(-1px, 0px);
								opacity: 0;
								box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
								width: 263px;
								height: 372px;
							}
							45% {
								opacity: 0;
							}
							100% {
								opacity: 1;
								// Keep it aligned to the left, only move up
								transform: translate(-1px, var(--teaser-container-height));
								box-shadow: 0 8px 40px rgba(0, 0, 0, 0.8);
								width: 384px;
								height: 438px;
							}
						}
					}
				}
			}
			&.is-closing {
				animation: collapseSmallTeaserFirst 0.4s cubic-bezier(0.1, 0, 0.2, 1) forwards;

				.preview-element {
					&.is-web {
						@keyframes collapseSmallTeaserFirst {
							0% {
								opacity: 1;
								transform: translate(0px, var(--teaser-container-height));
								box-shadow: 0 8px 40px rgba(0, 0, 0, 0.8);
								width: 384px;
								height: 438px;
							}
							45% {
								opacity: 0;
							}
							100% {
								transform: translate(0px, 0px);
								opacity: 0;
								box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
								width: 263px;
								height: 372px;
							}
						}
					}
				}
			}
		}
	}

	.teaser-small,
	.teaser-medium {
		&.is-last {
			&.is-previewing {
				animation: expandSmallTeaserLast 0.4s cubic-bezier(0.1, 0, 0.2, 1) forwards;
				border: 1px solid rgba(255, 255, 255, 0.1);

				.preview-element {
					&.is-web {
						@keyframes expandSmallTeaserLast {
							0% {
								transform: translate(
									calc(((var(--teaser-expanded-width) - var(--teaser-current-width)) * -1) / 2),
									0px
								);
								opacity: 0;
								box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
								width: var(--teaser-initial-width);
								height: var(--teaser-initial-height);
							}
							45% {
								opacity: 0;
							}
							100% {
								opacity: 1;
								// Move left by the width difference to keep right edge aligned
								// transform: translate(calc(100vw - var(--teaser-expanded-width) * -1 / 2), var(--teaser-container-height));
								transform: translate(
									calc((var(--teaser-expanded-width) - var(--teaser-current-width)) * -1),
									var(--teaser-container-height)
								);
								box-shadow: 0 8px 40px rgba(0, 0, 0, 0.8);
								width: var(--teaser-expanded-width);
								height: var(--teaser-expanded-height);
							}
						}
					}
				}
			}
			&.is-closing {
				animation: collapseSmallTeaserLast 0.4s cubic-bezier(0.1, 0, 0.2, 1) forwards;

				.preview-element {
					&.is-web {
						@keyframes collapseSmallTeaserLast {
							0% {
								opacity: 1;
								transform: translate(
									calc(var(--teaser-initial-width) - var(--teaser-expanded-width)),
									var(--teaser-container-height)
								);
								box-shadow: 0 8px 40px rgba(0, 0, 0, 0.8);
								width: var(--teaser-expanded-width);
								height: var(--teaser-expanded-height);
							}
							45% {
								opacity: 0;
							}
							100% {
								transform: translate(0px, 0px);
								opacity: 0;
								box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
								width: var(--teaser-initial-width);
								height: var(--teaser-initial-height);
							}
						}
					}
				}
			}
		}
	}

	.teaser-with-number {
		&.is-last {
			&.is-previewing {
				animation: expandSmallTeaserLastNumbered 0.4s cubic-bezier(0.1, 0, 0.2, 1) forwards;
				border: 1px solid rgba(255, 255, 255, 0.1);

				.preview-element {
					&.is-web {
						@keyframes expandSmallTeaserLastNumbered {
							0% {
								transform: translate(0, 0);
								opacity: 0;
								box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
								width: var(--teaser-initial-width);
								height: var(--teaser-initial-height);
							}
							45% {
								opacity: 0;
							}
							100% {
								opacity: 1;
								// Move left by the width difference to keep right edge aligned
								transform: translate(
									calc(calc(var(--teaser-initial-width) - var(--teaser-expanded-width))),
									var(--teaser-container-height)
								);
								box-shadow: 0 8px 40px rgba(0, 0, 0, 0.8);
								width: var(--teaser-expanded-width);
								height: var(--teaser-expanded-height);
							}
						}
					}
				}
			}
		}
	}
	.teaser-replica {
		position: absolute;
		background: black;
	}

	// preview effect reset
	.preview-element,
	.teaser {
		transition: transform 0.3s ease, box-shadow 0.3s ease;
		border-radius: 12px;
		overflow: hidden;

		.preview-section {
			position: absolute !important;
			bottom: 0;
			background: black;
			width: 100%;
		}
	}

	.preview-element {
		height: 100%;
		width: 100%;
		display: flex;
		flex-direction: column;

		.kwik-player {
			width: 100%;
			// height: 60%;
			height: auto;
			// min-height: 210px;
			min-height: 290px;
			max-height: 500px;
			object-fit: cover;
			object-position: center;
		}

		.preview-section {
			display: flex;
			flex-direction: column;
			gap: 4px;
			padding: 16px;
			overflow: hidden;
			transition: height 0.4s ease, opacity 0.4s ease;
			z-index: 1;
		}

		.fade-container {
			padding: 16px;
			cursor: pointer;
		}
	}

	/* Transition for the preview-section expansion */
	.expand-enter-active,
	.expand-leave-active {
		transition: height 0.3s ease;
		height: 300px;
	}
	.expand-enter-from,
	.expand-leave-to {
		height: 0%;
	}

	/* Transition for fading between img and video */
	.fade-enter-active,
	.fade-leave-active {
		transition: opacity 0.3s ease;
	}
	.fade-enter-from,
	.fade-leave-to {
		opacity: 0;
	}

	video {
		object-fit: cover;
		// border-radius: 12px;
		overflow: hidden;
		height: 100%;
		width: 100%;
	}

	.teaser-replica {
		z-index: 1000;
	}

	div[id^="kwik-player-preview"] {
		border-radius: 12px;
		overflow: hidden;
		height: 100%;
	}

	// #kwik-player-preview {
	// 	border-radius: 12px;
	// 	overflow: hidden;
	// }
</style>
